import { type Maybe } from "@oefr/utils/types"
import { hasProductTag, isProductOf, ProductCode } from "@/entities/product"
import { ProductTag } from "@/entities/productTag"
import { ProviderCalendarId } from "@/entities/providerCalendar"
import { EFFY_PACKAGE_PRODUCT_TAGS, type EffyPackage } from "@/features/effy/constants/effyPackage"
import { type FragmentOf, graphql, readFragment } from "@/graphql"
import { type SalesChannelWithTelesales } from "@/scalars/salesChannelChoices"

const graphQLFragments = {
    quotedProduct: graphql(
        `
            fragment isElectricityQuotedProductDisplayed_QuotedProduct on QuotedProduct {
                marketParams
                product {
                    ...isProductOf_Product
                    ...hasProductTag_Product
                }
            }
        `,
        [isProductOf.fragments.product, hasProductTag.fragments.product]
    ),
}

type MarketParams = {
    provider_calendar_id: string
}

type IsElectricityQuotedProductDisplayedProps = {
    quotedProduct: FragmentOf<typeof graphQLFragments.quotedProduct>
    providerCalendarId: string
    hasElectricVehicle: boolean
    isLinkyServiceLevel2: boolean
    salesChannel: SalesChannelWithTelesales
    isSeasonalHphcBase: boolean
    effyPackage: EffyPackage | null
}

export function isElectricityQuotedProductDisplayed(
    props: IsElectricityQuotedProductDisplayedProps
) {
    const quotedProduct = readFragment(graphQLFragments.quotedProduct, props.quotedProduct)

    const marketParams = quotedProduct.marketParams as Maybe<MarketParams>

    if (!quotedProduct.product || !marketParams) {
        return false
    }

    // Filter out EV products if the user isn't interrested in electric vehicle offer
    if (
        !props.hasElectricVehicle &&
        hasProductTag(quotedProduct.product, ProductTag.ELECTRIC_VEHICLE)
    ) {
        return false
    }

    // Filter out TRV product which is only quoted to display comparison
    if (isProductOf(quotedProduct.product, ProductCode.ECO_CONSO)) {
        return false
    }

    // Filter out products which requires a Linky level 2 meterPoint to be displayed
    if (
        !props.isLinkyServiceLevel2 &&
        hasProductTag(quotedProduct.product, ProductTag.LINKY_LEVEL_2)
    ) {
        return false
    }

    // Filter out products that does not fit the package for effy offers
    if (props.effyPackage) {
        const packageTag = EFFY_PACKAGE_PRODUCT_TAGS[props.effyPackage]

        // true only if this is the good product, else don't display it
        return packageTag ? hasProductTag(quotedProduct.product, packageTag) : true
    }

    if (
        props.providerCalendarId === ProviderCalendarId.HPHC_2_SAISONS &&
        props.salesChannel !== "TELESALES"
    ) {
        if (
            props.hasElectricVehicle &&
            hasProductTag(quotedProduct.product, ProductTag.ELECTRIC_VEHICLE)
        ) {
            return (
                !props.isSeasonalHphcBase &&
                marketParams.provider_calendar_id === ProviderCalendarId.HPHC
            )
        }

        // For COCON project, we want to display for HPHC_2_SAISONS either :
        // - When HPHC_2_SAISONS + isSeasonalHphcBase => COCON_SAISONNALISE_BASE
        // - When HPHC_2_SAISONS + isNotSeasonalHphcBase => COCON_SAISONNALISE_HPHC
        return props.isSeasonalHphcBase
            ? isProductOf(quotedProduct.product, ProductCode.COCON_SAISONNALISE_BASE_AOUT26_B)
            : isProductOf(quotedProduct.product, [
                  ProductCode.COCON_SAISONNALISE_HPHC_AOUT26_B,
                  ProductCode.COCON_SAISONNALISEE_AOUT26,
              ])
    }

    return marketParams.provider_calendar_id === props.providerCalendarId
}

isElectricityQuotedProductDisplayed.fragments = graphQLFragments
